/**
 * 入札中の札の一括取下 (AC-BSP-116) で、 画面が決める対象と入口の状態。
 *
 * 対象は、 共通ツールバーの範囲 (受渡日・発注主体区分・エリア) と、 通常入札タブ・ブロック入札タブで
 * 共有する一覧の絞り込み (AC-BSP-019 / 019a。 [[SharedBidListFilter]]) に合う入札中の札。 どちらのタブ
 * から押しても同じ対象になる。 タブ固有の絞り込み (通常入札のトランシェ・逆転のみ・制限超えのみ・
 * 申請セットの絞り込み、 ブロック入札の種別・提出ステータス) は AC-BSP-116 の絞り込み条件 (共通
 * ツールバーと AC-BSP-019 / 019a のフィルタ) に含まれないので掛けない。
 *
 * 対象の最終判定は backend が札ごとに行う。 画面は確認ダイアログに出す件数のために数えるだけで、
 * 新しい業務判定は持たない (取下できるか = backend の `withdrawable`、 作ったワークフローを理由に外すか =
 * 一覧応答の、 一括取下から外すワークフロー id の集合への所属)。
 */
import type { Timestamp } from '@bufbuild/protobuf/wkt'

import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import { blockRowBidIssuerType } from '~/pages/SpotBidding/models/blockBidRow'
import { canWithdrawBlockBidGroup } from '~/pages/SpotBidding/models/blockBidWithdrawAction'
import {
  type PageListScope,
  isInPageAreaScope,
  isInPageIssuerScope,
} from '~/pages/SpotBidding/models/pageIssuerScope'
import {
  type SharedBidListFilter,
  filterBlockGroupsBySharedListFilter,
  matchesSharedBidListFilter,
} from '~/pages/SpotBidding/models/sharedBidListFilter'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import type { SpotBidStatusCategoryInput } from '~/pages/SpotBidding/models/spotBidStatusCategory'
import { resolveSpotBidWithdrawAction } from '~/pages/SpotBidding/models/spotBidWithdrawAction'

/** 一括取下 RPC に渡す対象 1 件。 楽観ロックの基準値は取得した `Timestamp` のまま持つ。 */
export type BulkWithdrawTarget = {
  /** 通常入札は札の id、 ブロック入札はグループの id。 */
  id: string
  /** 札が属する申請セット (ブロック入札はグループ) の更新日時。 */
  expectedUpdatedAt: Timestamp
}

/** 一括取下の対象。 通常入札とブロック入札は別の RPC で送る。 */
export type BulkWithdrawRequest = {
  spot: readonly BulkWithdrawTarget[]
  block: readonly BulkWithdrawTarget[]
}

/** 対象を数える条件。 通常入札とブロック入札で同じ値を使う。 */
export type BulkWithdrawTargetConditions = {
  /** 共通ツールバーの範囲 (エリア・発注主体区分)。 */
  scope: PageListScope
  /** 両タブで共有する一覧の絞り込み (売/買・ステータス)。 */
  listFilter: SharedBidListFilter
  isBeforeDeadline: boolean
  /** 確定済ワークフローから開いていて画面が読み取り専用か。 */
  isWorkflowReadonly: boolean
  /**
   * 一括取下が作ったワークフローを理由に受け付けない札・グループの `workflowId` の集合 (AC-BSP-058)。
   * backend が一覧応答で返す値をそのまま使い、 通常入札とブロック入札で別の集合を渡す (通常入札は
   * ワークフローが確定済の札、 ブロック入札は確定済か記録したワークフローのケースが見つからないグループ。
   * どちらも backend の一括取下の判定と同じ)。
   */
  excludedWorkflowIds: ReadonlySet<string>
}

const isExcludedByWorkflow = (
  workflowId: string | undefined,
  excludedWorkflowIds: ReadonlySet<string>,
): boolean => workflowId !== undefined && excludedWorkflowIds.has(workflowId)

/** 通常入札の対象を数えるのに使う札の値。 */
export type BulkWithdrawSpotBidInput = Pick<
  SpotBidRow,
  'id' | 'areaCode' | 'issuerType' | 'direction' | 'withdrawable'
> &
  SpotBidStatusCategoryInput

/**
 * 通常入札の一括取下の対象にする札の id (受渡日の全札 `bids` から)。 共通ツールバーの範囲と共有の
 * 絞り込みに合い、 取下の操作が有効 ([[resolveSpotBidWithdrawAction]]) で、 札のワークフローが
 * 外す集合 (確定済) に入らないもの。
 */
export const resolveSpotBulkWithdrawTargetIds = (
  bids: readonly BulkWithdrawSpotBidInput[],
  {
    scope,
    listFilter,
    isBeforeDeadline,
    isWorkflowReadonly,
    excludedWorkflowIds,
  }: BulkWithdrawTargetConditions,
): string[] => {
  const ids = new Set<string>()
  for (const bid of bids) {
    if (
      !isInPageAreaScope(
        bid.areaCode,
        bid.issuerType,
        scope.selectedAreas,
        scope.unknownIssuerUnselectedAreas,
      ) ||
      !isInPageIssuerScope(bid.issuerType, scope.issuerScopeType)
    )
      continue
    if (!matchesSharedBidListFilter(bid, listFilter, !isBeforeDeadline))
      continue
    if (
      resolveSpotBidWithdrawAction({
        bid,
        isBeforeDeadline,
        isWorkflowReadonly,
      }) !== 'withdraw'
    )
      continue
    if (isExcludedByWorkflow(bid.workflowId, excludedWorkflowIds)) continue
    ids.add(bid.id)
  }
  return [...ids]
}

/**
 * ブロック入札の一括取下の対象にするグループの id (受渡日の全 Leg 行 `rows` から)。 共通ツールバーの
 * 範囲と共有の絞り込みに同じ Leg が合うグループ ([[filterBlockGroupsBySharedListFilter]]。 一覧と同じ
 * 規則で、 LINK / LOOP は Leg の 1 本が合えばグループ全体) のうち、 取下の操作が有効
 * ([[canWithdrawBlockBidGroup]]) で、 グループを作ったワークフローが外す集合 (確定済・ケースが見つからない)
 * に入らないもの。 件数は組を 1 件と数える。
 */
export const resolveBlockBulkWithdrawTargetIds = (
  rows: readonly BlockBidRow[],
  {
    scope,
    listFilter,
    isBeforeDeadline,
    isWorkflowReadonly,
    excludedWorkflowIds,
  }: BulkWithdrawTargetConditions,
): string[] => {
  const visible = filterBlockGroupsBySharedListFilter(
    rows,
    listFilter,
    !isBeforeDeadline,
    (leg) =>
      isInPageAreaScope(
        leg.areaCode,
        blockRowBidIssuerType(leg),
        scope.selectedAreas,
        scope.unknownIssuerUnselectedAreas,
      ) &&
      isInPageIssuerScope(blockRowBidIssuerType(leg), scope.issuerScopeType),
  )
  const ids = new Set<string>()
  for (const row of visible) {
    if (
      !canWithdrawBlockBidGroup({
        group: row,
        isBeforeDeadline,
        isWorkflowReadonly,
      })
    )
      continue
    if (isExcludedByWorkflow(row.workflowId, excludedWorkflowIds)) continue
    ids.add(row.groupId)
  }
  return [...ids]
}

/**
 * 一括取下の確認ダイアログを開く時点の対象を、 RPC に渡す形にする。 楽観ロックの基準値は、
 * ダイアログに件数を出した時点で読み込んでいた申請セット (ブロック入札はグループ) の更新日時。
 * 開いた後に他の操作で更新された申請セットの札を、 backend が拒否できるようにするため。
 *
 * 基準値を作れない対象 (申請セットが無い・更新日時が無い) は、 件数にも送信にも含めない。
 */
export const buildBulkWithdrawRequest = ({
  spotBidIds,
  blockGroupIds,
  bids,
  spotRequests,
  blockRequests,
}: {
  spotBidIds: readonly string[]
  blockGroupIds: readonly string[]
  bids: ReadonlyArray<Pick<SpotBidRow, 'id' | 'approvalRequestId'>>
  spotRequests: ReadonlyArray<{ id: string; updatedAt?: Timestamp }>
  blockRequests: ReadonlyArray<{ id: string; updatedAt?: Timestamp }>
}): BulkWithdrawRequest => {
  const requestIdOfBid = new Map(bids.map((b) => [b.id, b.approvalRequestId]))
  const spotUpdatedAt = new Map(spotRequests.map((r) => [r.id, r.updatedAt]))
  const blockUpdatedAt = new Map(blockRequests.map((r) => [r.id, r.updatedAt]))

  const spot: BulkWithdrawTarget[] = []
  for (const id of spotBidIds) {
    const requestId = requestIdOfBid.get(id)
    const expectedUpdatedAt =
      requestId === undefined ? undefined : spotUpdatedAt.get(requestId)
    if (expectedUpdatedAt !== undefined) spot.push({ id, expectedUpdatedAt })
  }
  const block: BulkWithdrawTarget[] = []
  for (const id of blockGroupIds) {
    const expectedUpdatedAt = blockUpdatedAt.get(id)
    if (expectedUpdatedAt !== undefined) block.push({ id, expectedUpdatedAt })
  }
  return { spot, block }
}

/**
 * ⋯ メニュー項目「入札中の札を一括で取下」 の状態。 押せない理由の優先順は spec AC-BSP-116 の表のとおり
 * (読み取り専用 → 入札締切後 → 通常入札未取得 → ブロック入札未取得 → 0 件)。 一覧を取得できていない間は
 * 件数を誤って見せたまま取り下げないため、 対象が 0 件でも 0 件の理由より先に示す (取得できていない一覧の
 * 札は 0 件と数えるため)。
 */
export type BulkWithdrawMenuState =
  | 'active'
  | 'readonly'
  | 'deadline'
  | 'spotUnresolved'
  | 'blockUnresolved'
  | 'empty'

export const resolveBulkWithdrawMenuState = ({
  isReadonly,
  isBeforeDeadline,
  isSpotUnresolved,
  isBlockUnresolved,
  targetCount,
}: {
  isReadonly: boolean
  isBeforeDeadline: boolean
  /** 通常入札の明細 / 一覧が取得できていない (取得中・取得失敗・未反映の差分) 間。 */
  isSpotUnresolved: boolean
  /** ブロック入札の明細 / 一覧が取得できていない間。 */
  isBlockUnresolved: boolean
  /** 通常入札の札の数 + ブロック入札のグループ数。 */
  targetCount: number
}): BulkWithdrawMenuState => {
  if (isReadonly) return 'readonly'
  if (!isBeforeDeadline) return 'deadline'
  if (isSpotUnresolved) return 'spotUnresolved'
  if (isBlockUnresolved) return 'blockUnresolved'
  if (targetCount === 0) return 'empty'
  return 'active'
}
